import React, { useEffect, useRef } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

type ModalVariant = 'success' | 'error' | 'warning' | 'info' | 'confirm';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  variant?: ModalVariant;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  details?: string[];
}

const variantConfig: Record<ModalVariant, {
  icon: React.FC<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  buttonClass: string;
}> = {
  success: {
    icon: CheckCircle,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  },
  error: {
    icon: XCircle,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    buttonClass: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
  },
  info: {
    icon: Info,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
  confirm: {
    icon: AlertTriangle,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
};

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  message,
  variant = 'info',
  onConfirm,
  confirmLabel,
  cancelLabel,
  details,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const config = variantConfig[variant];
  const Icon = config.icon;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-[fadeIn_150ms_ease-out]" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-[scaleIn_200ms_ease-out] overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`p-3 rounded-full ${config.iconBg}`}>
              <Icon className={`h-7 w-7 ${config.iconColor}`} />
            </div>
          </div>

          {/* Title */}
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {title}
            </h3>
          )}

          {/* Message */}
          <p className="text-sm text-gray-600 text-center leading-relaxed whitespace-pre-line">
            {message}
          </p>

          {/* Details list */}
          {details && details.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
              <ul className="space-y-1">
                {details.map((item, idx) => (
                  <li key={idx} className="text-xs text-gray-700 flex items-start gap-1.5">
                    <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex gap-3">
          {variant === 'confirm' ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                {cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={() => { onConfirm?.(); onClose(); }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.buttonClass}`}
              >
                {confirmLabel || 'Confirm'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className={`w-full px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.buttonClass}`}
            >
              {confirmLabel || 'OK'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};

export default Modal;
